import React, { useCallback, useEffect, useRef, useState, useContext } from 'react'
import { ModalActionTypes, Spinner } from '@acorns/web-components'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import { useMediaQuery } from 'react-responsive'
import AcornsMarkGreenSVG from 'assets/icons/acorns-mark-green.svg'

import withAgreements, { WithAgreementsProps } from 'components/HOCs/WithAgreements'
import { CURRENT_SIGN_UP_SCREEN_KEY } from 'app-constants'
import getDefaultEventData from 'components/Trackers/defaults'
import AgreementModal from 'components/AgreementModal'
import { OneTrustLink } from 'components/OneTrust/Link'
import {
  trackRegistrationCreateAccountScreenViewedPayload,
  trackRegistrationCreateAccountPasswordScreenViewedPayload,
  trackRegistrationCreateAccountPasswordNextButtonTappedPayload,
} from 'generated/trackingEvents'
import { PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import theme from 'theme'
import { getButtonTappedEvent, translateEventProps } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { getFlow } from 'utils/FlowProvider/utils'
import { shouldGrayOutCTACall } from 'utils/shouldGrayOutCTA'

import { ButtonHandle } from './components/GetStartedButton'
import {
  LinkInCTAContainer,
  StyledButton,
  StyledPageLayout,
  AcornsHeaderLogo,
  AcornImage,
} from './styled-components'
import { validate, validationErrorMap, validationSchema } from './logical/validation'
import { PromoCodesCta } from './components/PromoCodes/PromoCodesCta'
import { usePromoCodes } from './components/PromoCodes/usePromoCodes'
import { useFormik } from 'formikV2'
import { getSourceAttributes } from 'utils/sourceAttributes'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { getInitialValues } from './logical/formikHelpers'
import { useLocation } from 'react-router-dom'
import { withFlowRouter } from 'utils/FlowProvider'
import { withPrompt, PromptProps } from 'utils/prompts'
import { IErrorModalProps } from 'utils/Apollo/apolloErrorHandlingProvider'
import { FlowProviderProps } from 'utils/FlowProvider/types'
import { withBackend } from 'utils/backend'
import { useSignupFormSubmit } from './logical/useSignupFormSubmit'
import { EmailView } from './views/Email/index'
import { PasswordView } from './views/Password'
import { AcornsEarlyContext } from '../../providers/AcornsEarlyProvider'
import AcornsLogoGreen from 'assets/images/logo.png'
import { EnvironmentProps, withEnv } from 'utils/environment'

const submitKey = 'enter'
const submitCode = 13

type Props = WithAgreementsProps &
  FlowProviderProps &
  IErrorModalProps &
  PromptProps &
  EnvironmentProps

const Component = (props: Props) => {
  const location = useLocation()
  const { currentAgreement, isLoading } = props
  const { isAcornsEarlyGoldCustomerFlow, earlyCustomerEmail } = useContext(AcornsEarlyContext)

  const [passwordScreenViewedEventName, passwordScreenViewedPayload] =
    trackRegistrationCreateAccountPasswordScreenViewedPayload({
      type: 'create_account',
      funnelVersion: 'a4p1',
      funnel: undefined,
    })

  const analytics = useAnalyticsClient()
  const { getRemoteString } = useRemoteStrings()
  const [isEmailRowDisplayed, setIsEmailRowDisplayed] = useState(true)
  const isMobilePlatform = useMediaQuery({ maxWidth: `${theme.breakpoints.sm}px` })
  const getStartedButtonRef = useRef<ButtonHandle>(null)
  const [isEmailFieldFocused, setIsEmailFieldFocused] = useState(false)
  const [isEmailValid, setIsEmailValid] = useState(true)
  const [submitEmailCount, setSubmitEmailCount] = useState(0)

  const {
    appliedPromoCode,
    condensedTitle,
    description,
    hasPromoCodeQueryParam,
    isDrawerOpen,
    isErrorSubTextShown,
    isPromoCodeVerificationLoading,
    lastValidPromoCode,
    onCloseDrawer,
    promoCodeChannel,
    setIsDrawerOpen,
    termsUrl,
    validPromoCode,
    validatePromoCode,
    validationState,
  } = usePromoCodes()

  const { handleFormSubmit } = useSignupFormSubmit()

  const handleSubmit = async (values, { submitForm, setSubmitting }) => {
    await handleFormSubmit({
      values,
      setSubmitting,
      getAgreementIds: props.getAgreementIds,
      env: props.env,
      flow: getFlow(),
      submitForm,
      route: props.route,
    })
  }

  const initialValues = getInitialValues(location, earlyCustomerEmail)
  const formik = useFormik({
    enableReinitialize: true,
    isInitialValid: true,
    initialValues,
    onSubmit: handleSubmit,
    validate,
    validateOnChange: false,
    validationSchema,
  })

  const handleOnKeyDown = useCallback(
    (event) => {
      // Submit key is tied to keycode
      // keycode 13 is the Enter key
      // https://www.toptal.com/developers/keycode
      if (event.key?.toLowerCase() === submitKey || event.code === submitCode) {
        getStartedButtonRef?.current?.click()
      }
    },
    [getStartedButtonRef],
  )

  const onBackOverride = () => {
    if (!isEmailRowDisplayed) {
      setIsEmailRowDisplayed(true)
    }
  }

  const onBlur = () => {
    setIsEmailFieldFocused(false)
  }

  const onFocus = () => {
    setIsEmailFieldFocused(true)
    trackEvent('Field Clicked', {
      eventData: {
        object_name: 'registrationCreateAccountEmailFocus',
        screen: 'registrationCreateAccount',
        screen_name: 'registrationCreateAccount',
      },
      flow: getFlow(),
    })
  }

  const onSubmit = () => {
    const [eventName, payload] = trackRegistrationCreateAccountPasswordNextButtonTappedPayload({
      funnelVersion: 'a4p1',
      funnel: undefined,
      type: undefined,
      ctaTitle: undefined,
    })
    analytics.track(eventName, translateEventProps(payload, getSourceAttributes))

    // Attempt submit
    formik.submitForm()

    // If the number of attempts exceeds 2, display a dialog
    if (formik.submitCount > 2) {
      validationSchema()
        .validate(formik.values)
        .catch((error) => {
          const message = validationErrorMap.get(error.path)
          props.promptUser({
            title: 'Missed a step',
            message,
            actions: [
              {
                type: ModalActionTypes.primary,
                label: 'Okay',
              },
            ],
          })
        })
    }
  }

  const onFormChange = async (e) => {
    const { id, value, type } = e.target

    if (id === 'usResident') {
      trackEvent('Button Tapped', {
        eventData: translateEventProps(
          {
            ...getButtonTappedEvent(TRACKER_IDS.SIGNUP.US_RESIDENT_AGREEMENT_BUTTON),
            ...getDefaultEventData('Button Tapped'),
          },
          getSourceAttributes,
        ),
      })
    }

    await formik.setFieldValue(id, type === 'checkbox' ? e.target.checked : value)

    // Only validate the form if the user has attempted at least one submit
    if (formik.submitCount > 0) {
      formik.validateForm()
    }
  }

  const onEmailChange = async (e) => {
    formik.handleChange(e)

    if (submitEmailCount > 0) {
      const errors = await formik.validateForm()
      setIsEmailValid(!errors.email)
    }
  }

  useEffect(() => {
    if (isEmailRowDisplayed) {
      const [eventName, payload] = trackRegistrationCreateAccountScreenViewedPayload({
        funnel: undefined,
        defaultState: initialValues?.email ? 'prefilled' : undefined,
      })
      analytics.track(eventName, translateEventProps(payload, getSourceAttributes))
    } else {
      analytics.track(
        passwordScreenViewedEventName,
        translateEventProps(passwordScreenViewedPayload, getSourceAttributes),
      )
    }

    // used by Accessibility tracking
    sessionStorage.setItem(CURRENT_SIGN_UP_SCREEN_KEY, isEmailRowDisplayed ? 'email' : 'password')
    return () => sessionStorage.removeItem(CURRENT_SIGN_UP_SCREEN_KEY)
  }, [isEmailRowDisplayed, analytics])

  useEffect(() => {
    props.loadDataWithGQL()
  }, [])

  const shouldGrayOutPasswordCTA = shouldGrayOutCTACall({
    values: formik.values,
    validation: validate,
    ignoredKeys: ['email'],
  })

  const shouldGrayOutEmailCTA = shouldGrayOutCTACall({
    values: formik.values,
    validation: validate,
    ignoredKeys: ['password', 'usResident', 'partnerDisclosure'],
  })

  const isPromoCodeShowing = isEmailRowDisplayed && !isAcornsEarlyGoldCustomerFlow

  let footer = isEmailRowDisplayed && !isAcornsEarlyGoldCustomerFlow && (
    <PromoCodesCta appliedPromoCode={appliedPromoCode} setIsDrawerOpen={setIsDrawerOpen} />
  )

  let button = (
    <>
      {/** We can't simply unmount it. OneTrust will try to read it at the first render, if it's not in the DOM, the eventListeners will never be added. All we can do is hide it. */}
      <LinkInCTAContainer show={!isEmailRowDisplayed}>
        <OneTrustLink />
      </LinkInCTAContainer>
      {!isEmailRowDisplayed && (
        <StyledButton
          styleType={!formik.isValid || shouldGrayOutPasswordCTA ? 'invalid' : undefined}
          onPress={onSubmit}
          data-testid={TestIDs.SIGNUP.CREATE_ACCOUNT_BUTTON}
          disabled={formik.isSubmitting}
          buttonType={'submit'}
        >
          {formik.isSubmitting ? (
            <Spinner height={20} width={20} white={true} />
          ) : (
            <span>{getRemoteString({ key: 'registration.get_started.cta' })}</span>
          )}
        </StyledButton>
      )}
    </>
  )

  if (isEmailRowDisplayed) {
    button = null

    if (appliedPromoCode?.length > 0) {
      footer = null
    }
  }

  let navbarIcon = null

  if (isMobilePlatform) {
    navbarIcon = <AcornImage src={AcornsMarkGreenSVG} height={40} />
  } else if (!isMobilePlatform) {
    navbarIcon = <AcornsHeaderLogo src={AcornsLogoGreen} />
  }

  return (
    <>
      <StyledPageLayout
        isPromoCodeShowing={isPromoCodeShowing}
        eventObjectName={passwordScreenViewedPayload['object_name']}
        pageName={PageName.SIGNUP}
        navigationBar={{
          backOverride: onBackOverride,
          showBackButton: !isEmailRowDisplayed,
          centralComponent: navbarIcon,
        }}
        footer={footer}
        button={button}
      >
        {isEmailRowDisplayed && (
          <EmailView
            env={props.env}
            onEmailChange={onEmailChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleOnKeyDown}
            promoCodeProps={{
              appliedPromoCode,
              condensedTitle,
              description,
              hasPromoCodeQueryParam,
              isDrawerOpen,
              isErrorSubTextShown,
              isPromoCodeShowing,
              isPromoCodeVerificationLoading,
              lastValidPromoCode,
              onCloseDrawer,
              promoCodeChannel,
              setIsDrawerOpen,
              termsUrl,
              validatePromoCode,
              validationState,
            }}
            formik={formik}
            isEmailFieldFocused={isEmailFieldFocused}
            getStartedButtonRef={getStartedButtonRef}
            isEmailValid={isEmailValid}
            shouldGrayOutEmailCTA={shouldGrayOutEmailCTA}
            setIsEmailRowDisplayed={setIsEmailRowDisplayed}
            setIsEmailValid={setIsEmailValid}
            setSubmitEmailCount={setSubmitEmailCount}
            submitEmailCount={submitEmailCount}
            validPromoCode={validPromoCode}
            earlyCustomerEmail={earlyCustomerEmail}
          />
        )}
        {!isEmailRowDisplayed && (
          <PasswordView
            values={formik.values}
            onChange={onFormChange}
            errors={formik.errors}
            openAgreementModal={props.openAgreementModal}
            setFieldValue={formik.setFieldValue}
          />
        )}
      </StyledPageLayout>
      <AgreementModal
        title={currentAgreement?.name}
        isOpen={props.isModalOpen}
        onCloseClick={props.closeAgreementModal}
      >
        {isLoading ? <Spinner height={50} width={50} /> : currentAgreement?.document}
      </AgreementModal>
    </>
  )
}

export const Signup = withFlowRouter(withBackend(withAgreements(withPrompt(withEnv(Component)))))
